import { DOMSerializer, type Node as PMNode } from '@tiptap/pm/model';
import { NodeViewContent, type NodeViewProps, NodeViewWrapper } from '@tiptap/react';
import { useEffect, useRef, useState } from 'react';

import { ToastVariant } from '../../../components/toast';
import { trackError } from '../../../error/tracking';
import { captureException } from '../../../logger/capture-exception';
import { tGlobal } from '../../../services/translation';
import { copyToClipboard } from '../../clipboard';
import { useToast } from '../../contexts/toast-context';
import {
  type ContentTableCellHoverEvent,
  ContentTableEvent,
  type ContentTableOptions,
} from './content-table';
import { ContentTableUtilities } from './content-table-utilities';

export function ContentTableView(props: NodeViewProps) {
  const [shouldShowColumnAdder, setShouldShowColumnAdder] = useState(false);
  const [shouldShowRowAdder, setShouldShowRowAdder] = useState(false);
  const [topFloaterStyle, setTopFloaterStyle] = useState({ left: 0, width: 0 });
  const [leftFloaterStyle, setLeftFloaterStyle] = useState({ top: 0, height: 0 });
  const [isHoveringInTableArea, setIsHoveringInTableArea] = useState(false);
  const [activeColumnIndex, setActiveColumnIndex] = useState<number | null>(null);
  const [activeColumnIndexCandidate, setActiveColumnIndexCandidate] = useState<number | null>(null);
  const [activeRowIndex, setActiveRowIndex] = useState<number | null>(null);
  const [activeRowIndexCandidate, setActiveRowIndexCandidate] = useState<number | null>(null);
  const addToast = useToast();
  const tableRef = useRef<HTMLTableElement | null>(null);

  function updateTableState(generateTable: () => PMNode | null) {
    props.editor
      .chain()
      .command(function (args) {
        try {
          const newTable = generateTable();
          if (newTable == null) {
            return false;
          }
          const { tr } = args;
          const pos = props.getPos();
          tr.replaceWith(pos, pos + props.node.nodeSize, newTable);
          return true;
        } catch (err) {
          captureException(
            err,
            {
              file: 'src/shared/tiptap-custom-extensions/content-table-view.tsx',
              infoMessage: 'failed to update table state',
              method: 'updateTableState',
            },
            trackError
          );
          return false;
        }
      })
      .run();
  }

  function handleDuplicate() {
    props.editor
      .chain()
      .command(({ tr }) => {
        tr.insert(
          props.getPos() + props.node.nodeSize,
          props.node.type.create(props.node.attrs, props.node.content)
        );
        return true;
      })
      .run();
  }

  async function copyTableHtml() {
    const serializer = DOMSerializer.fromSchema(props.editor.schema.nodes.table.schema);
    const fragment = serializer.serializeFragment(props.node.content);
    const div = document.createElement('div');
    const table = document.createElement('table');
    table.appendChild(fragment);
    div.appendChild(table);

    const success = await copyToClipboard(div.getHTML());

    if (!success) {
      return addToast({
        title: tGlobal('clipboardError'),
        description: tGlobal('clipboardErrorDescription'),
        variant: ToastVariant.Error,
        id: Date.now(),
      });
    }

    addToast({
      title: tGlobal('copyToClipboard'),
      variant: ToastVariant.Success,
      id: Date.now(),
    });
  }

  function onMouseLeaveTableArea() {
    setIsHoveringInTableArea(false);
  }

  function onMouseEnterTableArea() {
    setIsHoveringInTableArea(true);
  }

  function highlightColumn() {
    setActiveColumnIndex(activeColumnIndexCandidate);
    if (activeColumnIndexCandidate == null) {
      return;
    }
    const firstCellInColumn = props.node.child(0).child(activeColumnIndexCandidate);
    let cellPosition: number | null = null;
    props.node.descendants(function (node, position) {
      if (node === firstCellInColumn) {
        cellPosition = position;
        return false;
      }
    });
    if (cellPosition == null) {
      return;
    }
    // selectColumn is in our control so we know that it's there, but typescript won't pick up the type hint
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    (props.editor.chain().focus() as any)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .selectColumn(props.getPos() + (cellPosition as number) + 1)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .run();
  }

  function highlightRow() {
    setActiveRowIndex(activeRowIndexCandidate);
    if (activeRowIndexCandidate == null) {
      return;
    }
    const row = props.node.child(activeRowIndexCandidate).child(0);
    let cellPosition: number | null = null;
    props.node.descendants(function (node, position) {
      if (node === row) {
        cellPosition = position;
        return false;
      }
    });
    if (cellPosition == null) {
      return;
    }
    // selectRow is in our control so we know that it's there, but typescript won't pick up the type hint
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    (props.editor.chain().focus() as any)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .selectRow(props.getPos() + (cellPosition as number) + 1)
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      .run();
  }

  function addRow(direction: 'up' | 'down', index?: number | null) {
    updateTableState(function () {
      let rows = [];
      if (index == null) {
        rows = [...props.node.children, props.editor.schema.nodes.tableRow.create({}, null)];
      } else if (direction === 'up') {
        rows =
          index === 0
            ? [props.editor.schema.nodes.tableRow.create({}, null), ...props.node.children]
            : [
                ...props.node.children.slice(0, index),
                props.editor.schema.nodes.tableRow.create({}, null),
                ...props.node.children.slice(index, props.node.children.length + 1),
              ];
      } else {
        rows =
          index === props.node.children.length - 1
            ? [...props.node.children, props.editor.schema.nodes.tableRow.create({}, null)]
            : [
                ...props.node.children.slice(0, index + 1),
                props.editor.schema.nodes.tableRow.create({}, null),
                ...props.node.children.slice(index + 1, props.node.children.length + 1),
              ];
      }
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function addColumn(direction: 'left' | 'right', index?: number | null) {
    updateTableState(function () {
      const rows = [];
      for (let r = 0; r < props.node.children.length; r++) {
        const row = props.node.child(r);
        // TODO: better detection, because this is naively assumming only row 0 could be header
        const cell = r === 0 ? 'tableHeader' : 'tableCell';
        if (index == null) {
          rows.push(
            props.editor.schema.nodes.tableRow.create(row.attrs, [
              ...row.children,
              props.editor.schema.nodes[cell].create(
                {},
                props.editor.schema.nodes.paragraph.create(null)
              ),
            ])
          );
          continue;
        }
        if (direction === 'left') {
          if (index === 0) {
            rows.push(
              props.editor.schema.nodes.tableRow.create(row.attrs, [
                props.editor.schema.nodes[cell].create({}, null),
                ...row.children,
              ])
            );
            continue;
          }
          rows.push(
            props.editor.schema.nodes.tableRow.create(row.attrs, [
              ...row.children.slice(0, index),
              props.editor.schema.nodes[cell].create(
                {},
                props.editor.schema.nodes.paragraph.create(null)
              ),
              ...row.children.slice(index, row.children.length + 1),
            ])
          );
          continue;
        }
        if (index === row.children.length - 1) {
          rows.push(
            props.editor.schema.nodes.tableRow.create(row.attrs, [
              ...row.children,
              props.editor.schema.nodes[cell].create(
                {},
                props.editor.schema.nodes.paragraph.create(null)
              ),
            ])
          );
          continue;
        }
        rows.push(
          props.editor.schema.nodes.tableRow.create(row.attrs, [
            ...row.children.slice(0, index + 1),
            props.editor.schema.nodes[cell].create(
              {},
              props.editor.schema.nodes.paragraph.create(null)
            ),
            ...row.children.slice(index + 1, row.children.length + 1),
          ])
        );
        continue;
      }
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function makeFirstRowHeader() {
    updateTableState(function () {
      const rows = [];
      for (let r = 0; r < props.node.children.length; r++) {
        const row = props.node.child(r);
        if (r !== 0) {
          rows.push(row);
          continue;
        }
        const newCells = [];
        for (let c = 0; c < row.children.length; c++) {
          newCells.push(props.editor.schema.nodes.tableHeader.create({}, row.child(c).content));
        }
        rows.push(props.editor.schema.nodes.tableRow.create({}, newCells));
      }
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function makeFirstColumnHeader() {
    updateTableState(function () {
      const rows = [];
      for (let r = 0; r < props.node.children.length; r++) {
        const row = props.node.child(r);
        const newCells = [];
        for (let c = 0; c < row.children.length; c++) {
          if (c !== 0) {
            newCells.push(row.child(c));
            continue;
          }
          newCells.push(props.editor.schema.nodes.tableHeader.create({}, row.child(c).content));
        }
        rows.push(props.editor.schema.nodes.tableRow.create({}, newCells));
      }
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function omitHeader() {
    updateTableState(function () {
      const rows = [];
      for (let r = 0; r < props.node.children.length; r++) {
        const row = props.node.child(r);
        const newCells = [];
        for (let c = 0; c < row.children.length; c++) {
          newCells.push(
            props.editor.schema.nodes.tableCell.create(row.child(c).attrs, row.child(c).content)
          );
        }
        rows.push(props.editor.schema.nodes.tableRow.create(row.attrs, newCells));
      }
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function deleteRow() {
    updateTableState(function () {
      const rows = props.node.children.filter(function (_row, index) {
        return index !== activeRowIndex;
      });
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  function deleteColumn() {
    updateTableState(function () {
      const rows = props.node.children.map(function (row, _index) {
        const cells = row.children.filter(function (_cell, cellIndex) {
          return cellIndex != activeColumnIndex;
        });
        return props.editor.schema.nodes.tableRow.create(row.attrs, cells);
      });
      return props.node.type.create(props.node.attrs, rows);
    });
  }

  useEffect(
    function () {
      if (tableRef.current != null) {
        tableRef.current.addEventListener(ContentTableEvent.CellHover, function (args) {
          const {
            left,
            width,
            top,
            height,
            node: hoveredCellNode,
          } = (args as ContentTableCellHoverEvent).detail;
          setTopFloaterStyle({
            left,
            width,
          });
          setLeftFloaterStyle({
            top,
            height,
          });
          props.node.descendants(function (tr, _trPos, _trParent, trIndex) {
            tr.descendants(function (cell, _cellPos, _cellParent, cellIndex) {
              if (cell === hoveredCellNode) {
                setShouldShowRowAdder(trIndex === props.node.childCount - 1);
                setShouldShowColumnAdder(cellIndex === tr.childCount - 1);
                setActiveColumnIndexCandidate(cellIndex);
                setActiveRowIndexCandidate(trIndex);
              }
            });
          });
        });
      }
    },
    [props.node]
  );
  const tableExtension = props.editor.extensionManager.extensions.find(
    (ext) => ext.name === 'table'
  );
  const isDraggable =
    tableExtension == null ? true : (tableExtension.options as ContentTableOptions).draggable;

  return (
    <NodeViewWrapper className="group relative mx-auto w-full gap-1">
      {isDraggable ? (
        <div
          className="font-body-2-m absolute -left-7 top-0 flex select-none text-branding-gray-600 group-hover:hidden"
          contentEditable={false}
        >
          T
        </div>
      ) : null}
      <div className="w-full p-10">
        <div
          className="relative inline-block"
          onMouseLeave={onMouseLeaveTableArea}
          onMouseEnter={onMouseEnterTableArea}
        >
          <table className="text-v2-slate12" ref={tableRef}>
            <NodeViewContent as="tbody" />
          </table>
          <ContentTableUtilities
            onDuplicate={handleDuplicate}
            onCopyTableHTML={copyTableHtml}
            onDelete={props.deleteNode}
            onAddColumnToTheEnd={function () {
              addColumn('right');
            }}
            onAddRowToTheEnd={function () {
              addRow('down');
            }}
            onHighlightColumn={highlightColumn}
            onHighlightRow={highlightRow}
            isHoveringInTableArea={isHoveringInTableArea}
            shouldShowColumnAdder={shouldShowColumnAdder}
            shouldShowRowAdder={shouldShowRowAdder}
            activeColumnIndex={activeColumnIndex}
            setActiveColumnIndex={setActiveColumnIndex}
            setActiveColumnIndexCandidate={setActiveColumnIndexCandidate}
            activeRowIndex={activeRowIndex}
            setActiveRowIndex={setActiveRowIndex}
            setActiveRowIndexCandidate={setActiveRowIndexCandidate}
            topFloaterStyle={topFloaterStyle}
            leftFloaterStyle={leftFloaterStyle}
            onAddColumn={function (direction) {
              addColumn(direction, activeColumnIndex);
            }}
            onAddRow={function (direction) {
              addRow(direction, activeRowIndex);
            }}
            onDeleteColumn={deleteColumn}
            onDeleteRow={deleteRow}
            onMakeFirstRowHeader={makeFirstRowHeader}
            onMakeFirstColumnHeader={makeFirstColumnHeader}
            onOmitHeader={omitHeader}
          />
        </div>
      </div>
    </NodeViewWrapper>
  );
}
